import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import { Player } from '@lottiefiles/react-lottie-player';
import MaintenancePage from './ui/main/MaintenancePage';

// 점검 모드 설정
// - 환경 변수 VITE_MAINTENANCE_MODE=true 로 설정하거나
// - 아래 MAINTENANCE_MODE_OVERRIDE를 true로 변경하면 점검 페이지가 표시됩니다
const MAINTENANCE_MODE_OVERRIDE = false; // 긴급 점검 시 true로 변경
const MAINTENANCE_MODE = MAINTENANCE_MODE_OVERRIDE || import.meta.env.VITE_MAINTENANCE_MODE === 'true';
import { useEffect, useState } from 'react';
import package_json from "./../package.json";
import Page from './const/Page';
import { AppUIContext } from './context/AppUIContext';
import { activateLanguage, defaultLanguage } from './locales/InitTranslation';
import { i18n } from '@lingui/core';
import { Message, SendFile } from './models/Message';
import { Room } from './models/Room';
import { RoomCall } from './models/RoomCall';
import { RoomItem } from './models/RoomItem';
import { Soribot } from './models/Soribot';
import { User } from './models/User';
import { Workspace } from './models/Workspace';
import { FilterType } from './models/local/Types';
import { Fire } from './services/FireClient';
import { My } from './services/My';
import { SuperAdmin } from './services/SuperAdmin';
import { twil } from './services/TwilioHelper';
import AppUrlListener from './services/Utils/AppUrlListener';
import { appLifecycle } from './services/Utils/AppLifecycle';
import { Util } from './services/Utils/Util';
import { MyNotifs } from './services/MyNotifs';
import { Badge as CapacitorBadge } from '@capawesome/capacitor-badge';
import CallHeader from './ui/components/CallHeader';
import CallInvitePopup from './ui/components/CallInvitePopup';
import LeftMenu from './ui/components/LeftMenu';
import { AppLayout } from './ui/components/AppLayout';
import ScreenRecordModal from './ui/components/ScreenRecordModal';
import AccountLanguagePage from './ui/main/AccountLanguage';
import AdminFlaggedPage from './ui/main/AdminFlaggedPage';
import BlockOtherUsersPage from './ui/main/BlockOtherUsersPage';

import ExternalPublicSharePage from './ui/main/ExternalPublicSharePage';
import FavoritesPage from './ui/main/FavoritesPage';
import LoginPage from './ui/main/LoginPage';
import MainPage, { GetAndroidPage, MainPageWelcome } from './ui/main/MainPage';
import MainPublicPage from './ui/main/MainPublicPage';
import RoomInvitationPage from './ui/main/RoomInvitationPage';
import SearchPage from './ui/main/SearchPage';
import TermsPage from './ui/main/TermsPage';
import UnavailablePage from './ui/main/UnavailablePage';
import MessageModal from './ui/rooms/MessageModal';
import RoomPage from './ui/rooms/RoomPage';
import RoomBotPage from './ui/rooms/right-menu/RoomBotPage';
import { TextAssistantModal } from './ui/rooms/right-menu/TextAssistantModal';
import MySoribotsPage from './ui/soribots/MySoribotsPage';
import PublicSoribotPage from './ui/soribots/PublicSoribotPage';
import SoribotSettingPage from './ui/soribots/SoribotSettingPage';

import WorkspaceInvitationPage from './ui/workspaces/WorkspaceInvitationPage';
import WorkspaceViewPage from './ui/workspaces/WorkspaceViewPage';
import WSAdminBotsPage from './ui/workspaces/admin/WSAdminBotsPage';
import WSAdminRoomsPage from './ui/workspaces/admin/WSAdminRoomsPage';
import WSAdminSubscriptionPage from './ui/workspaces/admin/WSAdminSubscriptionPage';
import SuperAdminLayout from './ui/super-admin/SuperAdminLayout';
import SuperAdminDashboard from './ui/super-admin/SuperAdminDashboard';
import SuperAdminWorkspaces from './ui/super-admin/SuperAdminWorkspaces';
import SuperAdminWorkspaceDetail from './ui/super-admin/SuperAdminWorkspaceDetail';
import SuperAdminLoginPage from './ui/super-admin/SuperAdminLoginPage';
import SuperAdminTiersPage from './ui/super-admin/SuperAdminTiersPage';
import SuperAdminTierEditPage from './ui/super-admin/SuperAdminTierEditPage';
import SuperAdminPromotionsPage from './ui/super-admin/SuperAdminPromotionsPage';
import SuperAdminInquiriesPage from './ui/super-admin/SuperAdminInquiriesPage';
import SuperAdminInvoicesPage from './ui/super-admin/SuperAdminInvoicesPage';
import SuperAdminSubscriptionsPage from './ui/super-admin/SuperAdminSubscriptionsPage';
import SuperAdminSettingsPage from './ui/super-admin/SuperAdminSettingsPage';
import SuperAdminIssuerPage from './ui/super-admin/SuperAdminIssuerPage';
import SuperAdminSecurityPage from './ui/super-admin/SuperAdminSecurityPage';
import DevAdminLayout from './ui/dev-admin/DevAdminLayout';
import DevAdminDashboard from './ui/dev-admin/DevAdminDashboard';
import DevAdminCollections from './ui/dev-admin/DevAdminCollections';
import DevAdminCleanup from './ui/dev-admin/DevAdminCleanup';
import DevAdminWorkspaces from './ui/dev-admin/DevAdminWorkspaces';
import DevAdminStorage from './ui/dev-admin/DevAdminStorage';
import DevAdminRooms from './ui/dev-admin/DevAdminRooms';
import DevAdminScheduler from './ui/dev-admin/DevAdminScheduler';
import { MessageProvider } from "./ui/main/NotificationModal";
import { Toaster } from "./components/ui/toaster";
import { EnterType } from './ui/rooms/right-menu/RoomFilesModal';
import { InitialNavigationWrapper } from './hooks/useInitialNavigationWrapper';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
// @ts-ignore
import DevRoutes from '@dev-routes';

if (Capacitor.getPlatform() !== 'web') {
  StatusBar.setOverlaysWebView({ overlay: false });
  StatusBar.setStyle({ style: Style.Light });
  StatusBar.setBackgroundColor({ color: '#ffffff' });
}

// Android용 간단한 토스트 함수
function showAndroidToast(message: string) {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.cssText = `
    position: fixed;
    bottom: 80px;
    left: 24px;
    right: 24px;
    background: rgba(0, 0, 0, 0.85);
    color: white;
    padding: 14px 20px;
    border-radius: 12px;
    font-size: 14px;
    z-index: 99999;
    animation: fadeInOut 2s ease-in-out;
    text-align: center;
    line-height: 1.4;
  `;

  // 애니메이션 스타일 추가
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeInOut {
      0% { opacity: 0; transform: translateY(20px); }
      15% { opacity: 1; transform: translateY(0); }
      85% { opacity: 1; transform: translateY(0); }
      100% { opacity: 0; transform: translateY(-10px); }
    }
  `;
  document.head.appendChild(style);
  document.body.appendChild(toast);

  setTimeout(() => {
    toast.remove();
    style.remove();
  }, 2000);
}

// Android 뒤로가기 버튼 핸들러 (두 번 누르면 종료)
if (Capacitor.getPlatform() === 'android') {
  let lastBackPress = 0;
  const BACK_PRESS_THRESHOLD = 2000; // 2초 내에 다시 누르면 종료

  CapApp.addListener('backButton', async ({ canGoBack }) => {
    // 뒤로 갈 수 있으면 브라우저 히스토리 뒤로가기
    if (canGoBack) {
      window.history.back();
      return;
    }

    // 메인 화면에서 뒤로가기 누르면
    const now = Date.now();
    if (now - lastBackPress < BACK_PRESS_THRESHOLD) {
      // 2초 내에 다시 누르면 앱 종료
      await CapApp.exitApp();
    } else {
      // 처음 누르면 토스트 표시
      lastBackPress = now;
      showAndroidToast(i18n._('Press back again to exit'));
    }
  });
}

var soribotPages: SoribotSettingPage[] = ['details', 'datasources', 'knowledgeviewer', 'freechat', 'testcases', 'testruns', 'suggestions', 'rooms', 'public', 'api', 'messages'];

const App: React.FC = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const a = queryParams.get('a');
  const [loginState, setLoginState] = useState<'progress' | 'authenticated' | 'loggedout'>('progress');
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState<FilterType>('none');
  const [iframeItem, setIframeItem] = useState<RoomItem | null>(null);
  const [messageToShow, setMessageToShow] = useState<Message | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [soribots, setSoribots] = useState<Soribot[]>([]);
  const [uploadFiles, setUploadFiles] = useState<SendFile[]>([]);
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [enterType, setEnterType] = useState<EnterType>('link');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeCalls, setActiveCalls] = useState(new Map<string, RoomCall>());
  const [roomsLoaded, setRoomsLoaded] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    // Super Admin 상태 초기화 (sessionStorage에서 복원)
    if (SuperAdmin.isLoggedIn) {
      setIsSuperAdmin(true);
      setLoginState('authenticated');
    }

    Fire.auth.onLoginStateChanged = (user: User | null) => {
      if (user) {
        setLoginState('authenticated');
        // Sync Super Admin state from SuperAdmin class
        setIsSuperAdmin(SuperAdmin.isLoggedIn);
        Fire.getUserBlocks(user.id).then(list => {
          list.forEach(block => My.UserBlocks.set(block.blockedUser.id, block));
        })
        if (My.isLoggedIn()) {
          const userLang = My.user.lang || defaultLanguage;
          if (userLang !== defaultLanguage) {
            activateLanguage(userLang);
          }
        }
      }
      else {
        // Super Admin 로그인 상태면 loggedout으로 변경하지 않음
        if (!SuperAdmin.isLoggedIn) {
          setLoginState('loggedout');
        }
      }
    }

    window.addEventListener('appinstalled', async () => {
      Fire.auth.handleOnAuthStateChanged();
    });

    Fire.auth.handleOnAuthStateChanged();
    return twil.subscribeToTwilioUpdates((e) => {
      if (e?.type == 'StateChange')
        setConnected(twil.isConnected())
    });
  }, [])

  useEffect(() => {
    if (loginState == 'authenticated') {
      // 구독 설정 함수
      const setupSubscriptions = () => {
        const unsub1 = Fire.ws.subscribeToWorkspaces(My.user.id, (wsList) => {
          setWorkspaces(wsList);
        });
        const unsub2 = Fire.bot.subscribeToMySoribots(My.user.id, (bots) => {
          setSoribots(bots.sort((a, b) => Util.dateSortFunc(a.created, b.created)));
        });
        const unsub3 = Fire.call.subscribeToRoomCalls(My.user.id, (calls) => {
          const map = new Map<string, RoomCall>();
          calls.forEach(c => map.set(c.id, c));
          setActiveCalls(map);
        });
        // 알림 구독 추가 (App 레벨에서 관리하여 왼쪽 메뉴가 닫혀있어도 업데이트됨)
        const unsub4 = Fire.subscribeToNewNotifications(My.user.id, (notifs) => {
          MyNotifs.setNotifications(notifs, My.RoomSettings);
          // ✅ OS 뱃지도 즉시 동기화 (MyNotifs.NrOfNew가 setNotifications에서 갱신된 후)
          if (Capacitor.isNativePlatform()) {
            CapacitorBadge.set({ count: MyNotifs.NrOfNew });
          }
        });

        // AppLifecycle에 구독 등록 (백그라운드 시 자동 해제)
        appLifecycle.registerSubscription('workspaces', unsub1);
        appLifecycle.registerSubscription('soribots', unsub2);
        appLifecycle.registerSubscription('roomCalls', unsub3);
        appLifecycle.registerSubscription('notifications', unsub4);
      };

      // 초기 구독 설정
      setupSubscriptions();

      // 포그라운드 복귀 시 재구독
      const unsubLifecycle = appLifecycle.onStateChange((isBackground) => {
        if (!isBackground && loginState === 'authenticated') {
          console.log('[App] Foreground detected, re-subscribing...');
          setupSubscriptions();
        }
      });

      return () => {
        appLifecycle.unsubscribeAll();
        unsubLifecycle();
      }
    }
  }, [loginState])

  return (
    <>
      {/* 점검 모드일 때 점검 페이지만 표시 */}
      {MAINTENANCE_MODE ? (
        <MaintenancePage />
      ) : (
        <div className={`h-full w-full absolute inset-0 text-left ${connected ? 'connected' : ''}`}>
          {loginState == 'progress' &&
            <div className="flex flex-col items-center justify-center h-full w-full bg-white">
              <div id="just-a-moment" className="relative flex flex-col items-center">
                <Player autoplay keepLastFrame src="assets/splash.json" style={{ height: '160px', width: '160px' }}></Player>
                <p className='version absolute -bottom-8 text-xs text-gray-500'>v{package_json.version}</p>
              </div>
              <div className="w-64 h-1 bg-gray-100 mt-8 rounded overflow-hidden">
                <div className="h-full bg-primary animate-progress"></div>
              </div>
            </div>
          }
          {loginState != 'progress' &&
            <>
              <BrowserRouter>
                <AppUrlListener />
                <Toaster />
                <CallInvitePopup />
                <CallHeader />
                <ScreenRecordModal />
                {loginState == 'authenticated' &&
                  <>
                    <TextAssistantModal />
                    <AppUIContext.Provider value={{
                      filter, setFilter, iframeItem, setIframeItem,
                      messageToShow, setMessageToShow, workspaces, setWorkspaces,
                      rooms, setRooms, soribots, setSoribots,
                      uploadFiles, setUploadFiles, showFilesModal, setShowFilesModal, enterType, setEnterType,
                      isMobileMenuOpen, setIsMobileMenuOpen, activeCalls, setActiveCalls, roomsLoaded, setRoomsLoaded
                    }}>
                      <InitialNavigationWrapper
                        onRoomsLoaded={(rooms) => setRooms(rooms)}
                        setRoomsLoaded={setRoomsLoaded}
                      />
                      <MessageProvider>
                        <Routes>
                          {/* Dev Routes (ignored in git) */}
                          {DevRoutes.map((route: any) => (
                            <Route key={route.path} path={route.path} element={route.element} />
                          ))}
                          {/* Super Admin Routes - Independent layout, no AppLayout */}
                          {/* Check both React state and My.isSuperAdmin for immediate access after login */}
                          <Route path="/super-admin" element={(isSuperAdmin || SuperAdmin.isLoggedIn) ? <SuperAdminLayout /> : <Navigate to="/main" replace />}>
                            <Route index element={<SuperAdminDashboard />} />
                            <Route path="workspaces" element={<SuperAdminWorkspaces />} />
                            <Route path="workspaces/:workspaceId" element={<SuperAdminWorkspaceDetail />} />
                            <Route path="revenue/subscriptions" element={<SuperAdminSubscriptionsPage />} />
                            <Route path="revenue/invoices" element={<SuperAdminInvoicesPage />} />
                            <Route path="tiers" element={<SuperAdminTiersPage />} />
                            <Route path="tiers/:tierId" element={<SuperAdminTierEditPage />} />
                            <Route path="promotions" element={<SuperAdminPromotionsPage />} />
                            <Route path="inquiries" element={<SuperAdminInquiriesPage />} />
                            <Route path="settings" element={<SuperAdminSettingsPage />} />
                            <Route path="settings/issuer" element={<SuperAdminIssuerPage />} />
                            <Route path="settings/security" element={<SuperAdminSecurityPage />} />
                          </Route>
                          {/* Super Admin Login - Independent page, no AppLayout */}
                          <Route path="/super-login" element={<SuperAdminLoginPage />} />
                          {/* Developer Admin Routes */}
                          <Route path="/dev-admin" element={<DevAdminLayout />}>
                            <Route index element={<DevAdminDashboard />} />
                            <Route path="collections" element={<DevAdminCollections />} />
                            <Route path="workspaces" element={<DevAdminWorkspaces />} />
                            <Route path="rooms" element={<DevAdminRooms />} />
                            <Route path="cleanup" element={<DevAdminCleanup />} />
                            <Route path="storage" element={<DevAdminStorage />} />
                            <Route path="scheduler" element={<DevAdminScheduler />} />
                          </Route>
                          {/* Normal User Routes with AppLayout */}
                          <Route path="*" element={
                            <AppLayout>
                              <MessageModal />
                              <div id="main" className="ion-page h-full">
                                <Routes>
                                  {/* Default redirect - always to /main, no auto admin redirect */}
                                  <Route path="/" element={<Navigate to="/main" replace />} />
                                  <Route path="/main" element={<MainPage />} />
                                  <Route path="/welcome" element={<MainPageWelcome />} />

                                  <Route path="/room/:roomId" element={<RoomPage />} />
                                  <Route path="/room/:roomId/bots" element={<RoomBotPage />} />
                                  <Route path="/room/:roomId/favorites" element={<FavoritesPage />} />
                                  <Route path="/ws/:workspaceId" element={<WorkspaceViewPage />} />
                                  <Route path="/ws-admin/rooms/:workspaceId" element={<WSAdminRoomsPage />} />
                                  <Route path="/ws-admin/subscription/:workspaceId" element={<WSAdminSubscriptionPage />} />
                                  <Route path="/ws-admin/bots/:workspaceId" element={<WSAdminBotsPage />} />
                                  <Route path="/login" element={<Navigate to="/main" replace />} />

                                  <Route path="/terms" element={<TermsPage />} />
                                  <Route path="/admin_flagged" element={<AdminFlaggedPage />} />
                                  <Route path="/invitation/:invitationId" element={<RoomInvitationPage />} />
                                  <Route path="/ws-invite/:invitationKey" element={<WorkspaceInvitationPage />} />
                                  <Route path="/get_android" element={<GetAndroidPage />} />
                                  <Route path="/search" element={<SearchPage />} />
                                  <Route path="/search/:roomId" element={<SearchPage />} />
                                  <Route path={`${Page.publicSharePage}/:linkId`} element={<ExternalPublicSharePage />} />
                                  <Route path={Page.my_soribots} element={<MySoribotsPage />} />
                                  {soribotPages.map(page => (
                                    <Route key={page} path={`${Page.soribot}/${page}/:botId`} element={<SoribotSettingPage page={page} />} />
                                  ))}
                                  <Route path={`${Page.publicBotPage}/:publicId`} element={<PublicSoribotPage />} />
                                  <Route path="*" element={<UnavailablePage />} />
                                </Routes>
                              </div>
                            </AppLayout>
                          } />
                        </Routes>
                      </MessageProvider>
                    </AppUIContext.Provider>
                  </>
                }
                {loginState == 'loggedout' && <>
                  <div id="main" className="h-full w-full bg-[#F0F0F2]">
                    <Routes>
                      {/* Super Admin Routes - 로그아웃 상태에서도 접근 가능 */}
                      {SuperAdmin.isLoggedIn && (
                        <Route path="/super-admin/*" element={<SuperAdminLayout />}>
                          <Route index element={<SuperAdminDashboard />} />
                          <Route path="workspaces" element={<SuperAdminWorkspaces />} />
                          <Route path="workspaces/:workspaceId" element={<SuperAdminWorkspaceDetail />} />
                          <Route path="subscriptions" element={<SuperAdminWorkspaces />} />
                          <Route path="documents" element={<SuperAdminDashboard />} />
                          <Route path="settings" element={<SuperAdminDashboard />} />
                        </Route>
                      )}
                      <Route path="/" element={<Navigate to="/login" replace />} />
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/super-login" element={<SuperAdminLoginPage />} />
                      <Route path="/main" element={<Navigate to="/login" replace />} />
                      <Route path="/terms" element={<TermsPage />} />
                      <Route path="/invitation/:invitationId" element={<RoomInvitationPage />} />
                      <Route path="/ws-invite/:invitationKey" element={<WorkspaceInvitationPage />} />
                      <Route path={`${Page.publicSharePage}/:linkId`} element={<ExternalPublicSharePage />} />
                      <Route path={`${Page.publicBotPage}/:publicId`} element={<PublicSoribotPage />} />
                      <Route path="*" element={<Navigate to={SuperAdmin.isLoggedIn ? "/super-admin" : "/login"} replace />} />
                    </Routes>
                  </div>
                </>
                }
              </BrowserRouter>
            </>
          }
        </div>
      )}
    </>
  );
};

export default App;
